/**
 * Motor de teclado — layout MIDI, geometría de teclas y rangos por mano.
 */

import type { NoteName } from "./guitarEngine";
import { semitonesToNote } from "./guitarEngine";

export type KeyKind = "white" | "black";

export interface PianoKeyDef {
  midi: number;
  kind: KeyKind;
  /** Índice de tecla blanca (solo blancas, para posición X) */
  whiteIndex: number;
  note: NoteName;
  octave: number;
  label: string;
}

export interface KeyboardRange {
  startMidi: number;
  endMidi: number;
  label: string;
}

/** 61 teclas: C2 (36) – C7 (96) — rango didáctico web. */
export const KEYBOARD_61: KeyboardRange = {
  startMidi: 36,
  endMidi: 96,
  label: "61 teclas",
};

export const MIDDLE_C = 60;
export const HAND_SPLIT_MIDI = MIDDLE_C;

const BLACK_PCS = new Set([1, 3, 6, 8, 10]);

export function isBlackKey(midi: number): boolean {
  const pc = ((midi % 12) + 12) % 12;
  return BLACK_PCS.has(pc);
}

function whiteIndexForMidi(midi: number, range: KeyboardRange = KEYBOARD_61): number {
  let idx = 0;
  for (let m = range.startMidi; m <= midi; m++) {
    if (!isBlackKey(m)) idx++;
  }
  return Math.max(0, idx - 1);
}

export function buildKeyboardKeys(range: KeyboardRange = KEYBOARD_61): PianoKeyDef[] {
  const keys: PianoKeyDef[] = [];
  for (let midi = range.startMidi; midi <= range.endMidi; midi++) {
    const note = semitonesToNote(midi, false);
    const octave = Math.floor(midi / 12) - 1;
    keys.push({
      midi,
      kind: isBlackKey(midi) ? "black" : "white",
      whiteIndex: isBlackKey(midi) ? -1 : whiteIndexForMidi(midi, range),
      note,
      octave,
      label: `${note}${octave}`,
    });
  }
  return keys;
}

export function countWhiteKeys(range: KeyboardRange = KEYBOARD_61): number {
  let n = 0;
  for (let m = range.startMidi; m <= range.endMidi; m++) {
    if (!isBlackKey(m)) n++;
  }
  return n;
}

/** Tecla negra inmediatamente a la derecha de esta blanca (centrada en el borde). */
export function blackKeyAfterWhite(
  whiteMidi: number,
  range: KeyboardRange = KEYBOARD_61
): number | null {
  const black = whiteMidi + 1;
  if (black > range.endMidi || black < range.startMidi) return null;
  return isBlackKey(black) ? black : null;
}

/** Posición X relativa 0–1 (referencia; el layout en UI usa grid por tecla blanca). */
export function keyPositionX(midi: number, range: KeyboardRange = KEYBOARD_61): number {
  const whites = countWhiteKeys(range);
  if (isBlackKey(midi)) {
    const prev = midi - 1;
    const next = midi + 1;
    return (keyPositionX(prev, range) + keyPositionX(next, range)) / 2;
  }
  const wi = whiteIndexForMidi(midi, range);
  return whites <= 1 ? 0.5 : wi / (whites - 1);
}

export function midiInRange(midi: number, range: KeyboardRange = KEYBOARD_61): boolean {
  return midi >= range.startMidi && midi <= range.endMidi;
}
